import {Box} from '@chakra-ui/react';
import {HeaderWrapper} from 'components/layouts';
import {colors} from 'components/providers';
import {ContextType, FC, useCallback, useEffect, useRef} from 'react';
import {ScrollMenu, VisibilityContext} from 'react-horizontal-scrolling-menu';
import {hiddenHorizontalScroll} from 'theme';
import {ICategories} from 'types';

import {useScrollCategories} from '../hooks';
import {CategoryCardWrapper, ItemNav} from './components';

interface IProps {
  categoryList: ICategories[];
  isSearchable: boolean;
}

export type scrollVisibilityApiType = ContextType<typeof VisibilityContext>;

export const CategorySubheader: FC<IProps> = ({categoryList, isSearchable}) => {
  const {activeCategory, setActiveCategory} = useScrollCategories();

  useEffect(() => {
    if (activeCategory) return;
    if (!categoryList.length) return;

    const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    if (hash && hash.startsWith('category-')) {
      setActiveCategory(hash);
      return;
    }

    if (!isSearchable) setActiveCategory(`category-${categoryList[0].id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchable, categoryList, activeCategory]);

  useEffect(() => {
    const itemId = activeCategory?.replace('category-', '');
    if (!itemId) return;

    scrollToItem(itemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const apiRef = useRef({} as scrollVisibilityApiType);

  const handleItemClick =
    (itemId: string) =>
    ({getItemById, scrollToItem}: scrollVisibilityApiType) => {
      scrollToItem(getItemById(itemId), 'smooth', 'center', 'nearest');
    };

  const scrollToItem = useCallback((itemId: string) => {
    const item = apiRef.current?.getItemElementById(itemId);
    if (item) apiRef.current?.scrollToItem?.(item, 'smooth', 'center');
  }, []);
  if (!categoryList?.length) return null;

  return (
    <Box borderTop="1px" w="100%" borderColor={colors.borderGray}>
      <HeaderWrapper boxShadow="header" as="nav" innerbox={{p: 0}}>
        <Box
          w={['full']}
          sx={{
            '.provider-scroll-item:first-of-type': {
              ml: '15px',
            },
            ...hiddenHorizontalScroll,
          }}
        >
          <ScrollMenu itemClassName="provider-scroll-item" apiRef={apiRef} options={{ratio: 0.01}}>
            {categoryList.map((item) => {
              const isActive = `category-${item.id}` === activeCategory;
              return (
                <CategoryCardWrapper itemId={`${item.id}`} key={`${item.id}`} onClick={handleItemClick(`${item.id}`)}>
                  <ItemNav {...item} activeCategory={isActive} setActiveCategory={setActiveCategory} />
                </CategoryCardWrapper>
              );
            })}
          </ScrollMenu>
        </Box>
      </HeaderWrapper>
    </Box>
  );
};
